import { datadogRum } from '@datadog/browser-rum';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IconSvg, Modal, Text } from '@/lib/v2/components';
import { EmailTriggerIcon } from '@/lib/v2/icons/solid';
import { ModalSingleInput } from '@/lib/v2/organisms';
import CardAction, { CardActionProps } from '@/lib/v2/organisms/CardAction/CardAction';

import { ETriggerActivityType } from '@/src/infrastructure/interfaces/IAutomation.interface';
import { useRulesService } from '@/src/infrastructure/Protocol/Rules/useRulesService';

import { useCreateRule } from '@/modules/AutomationModuleNew/hooks/useCreateRule';
import { useRulesValidations } from '@/modules/AutomationModuleNew/hooks/useRulesValidations';
import {
  RulesAPIIcon,
  RulesDateIcon,
  RulesExternalURLIcon,
  RulesFormIcon,
  RulesTagAssociatedIcon,
} from '@/modules/RulesModule/images/icons';
import { FieldDTO, PayloadSaveRules, RuleTypes } from '@/modules/RulesModule/interfaces';

const TRANSLATE_RULES_TYPES = 'ALL_CARDS_ACTIONS.RULES_TYPES';

interface SelectRuleTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SelectRuleTypeModal = ({ isOpen, onClose }: SelectRuleTypeModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rulesService = useRulesService();
  const [ruleType, setRuleType] = useState<RuleTypes | null>(null);
  const { VALIDATION_RULES } = useRulesValidations();
  const {
    createModalIsOpen,
    handleToggleCreateModal,
    isLoading,
    createRule,
    setRuleType: setRuleTypeAutomation,
    setIsLoading,
  } = useCreateRule(true);

  const saveDefaultRuleTypeAfterCreate = useCallback(
    async (actionId: number) => {
      if (!ruleType) return;

      //cspell:disable
      const templatePayload = {
        acumuladaCantidad: null,
        campoDTO: {} as FieldDTO,
        camposPersonalizadosDTO: [],
        elementoId: actionId,
        elementosDTO: [],
        elementosHnosCantidad: 0,
        enviarCadaAnio: null,
        envioRecurrente: false,
        fechaEnvio:
          ruleType === RuleTypes.DATE || ruleType === RuleTypes.TAG
            ? '1|mes|antes|dia'
            : 'de inmediato',
        tag_id: [],
        tipo: 'Clicks',
        urlDestino: null,
        urlDestinoError: null,
      };
      //cspell:disable
      const payloadSave = {
        ...templatePayload,
        desencadenante: ruleType,
      } as PayloadSaveRules;

      await rulesService.saveRule(payloadSave);
    },
    [ruleType, rulesService]
  );

  const handleOpenCreateActionNameModal = useCallback(
    (ruleType: RuleTypes, ruleTypeAutomation: ETriggerActivityType) => {
      setRuleType(ruleType);
      setRuleTypeAutomation(ruleTypeAutomation);
      datadogRum.addAction('automation.onCreateRule', {
        ruleType,
      });
      handleToggleCreateModal();
    },
    [handleToggleCreateModal, setRuleTypeAutomation]
  );

  const handleCloseCreateModal = useCallback(() => {
    handleToggleCreateModal();
    onClose();
  }, [handleToggleCreateModal, onClose]);

  const handleCreateRule = useCallback(
    async (name: string) => {
      const response = await createRule(name);
      if (!response?.isValidInput) return response;

      const { campaignId, id } = response.data;
      setIsLoading(true);
      await saveDefaultRuleTypeAfterCreate(id);
      setIsLoading(false);
      onClose();
      navigate(`${campaignId}/action/${id}/email-trigger`);
    },
    [createRule, navigate, onClose, saveDefaultRuleTypeAfterCreate, setIsLoading]
  );

  const cardActionsList = useMemo<CardActionProps[]>(
    () => [
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.rulesDate.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.rulesDate.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'rules-date',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesDateIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () =>
          handleOpenCreateActionNameModal(RuleTypes.DATE, ETriggerActivityType.TRIGGER_DATE),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.tagAssociated.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.tagAssociated.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'tag-associated',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesTagAssociatedIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () =>
          handleOpenCreateActionNameModal(RuleTypes.TAG, ETriggerActivityType.ASSOCIATED_TAG),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.externalURL.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.externalURL.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'external-url',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesExternalURLIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () =>
          handleOpenCreateActionNameModal(
            RuleTypes.EXTERNAL_CALL,
            ETriggerActivityType.EXTERNAL_URL_CALL
          ),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromAPI.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromAPI.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'from-api',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesAPIIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () =>
          handleOpenCreateActionNameModal(
            RuleTypes.API_INTEGRATOR,
            ETriggerActivityType.API_INTEGRATOR
          ),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromForm.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromForm.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'from-form',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesFormIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () =>
          handleOpenCreateActionNameModal(RuleTypes.WEB_FORM, ETriggerActivityType.FORM),
      },
    ],
    [t, handleOpenCreateActionNameModal]
  );

  return (
    <>
      <Modal noPadding withAuto open={isOpen && !createModalIsOpen} onClose={onClose}>
        <div className={'flex h-auto max-h-[95vh] w-[90vw] flex-col md:w-[50vw]'}>
          <div className="flex w-full items-center gap-2 border-b px-6 py-4">
            <IconSvg
              rounded
              bgColor="primary"
              height="40px"
              svgComponent={<EmailTriggerIcon />}
              width="40px"
              withPadding="small"
            />
            <Text fontWeight="medium" variant="sub-headline">
              {t('ALL_CARDS_ACTIONS.RULES_TYPES.title')}
            </Text>
          </div>
          <div className="grid grid-cols-2 gap-4 p-6 md:grid-cols-3">
            {cardActionsList.map((cardAction) => {
              return <CardAction key={cardAction.id} {...cardAction} justifyActionButton="end" />;
            })}
          </div>
        </div>
      </Modal>
      {createModalIsOpen && (
        <ModalSingleInput
          isLoading={isLoading}
          isOpen={createModalIsOpen}
          rules={VALIDATION_RULES}
          title={t('SelectRuleTypeModal.createRule.title')}
          onClose={handleCloseCreateModal}
          onSubmit={handleCreateRule}
        />
      )}
    </>
  );
};

export default memo(SelectRuleTypeModal);
